// The two-option selector (AppIconChipToggle, role="group") whose buttons read exactly these labels
function findToggle(...labels) {
	const want = labels.map(l => l.toLowerCase())
	return [...document.querySelectorAll('[role="group"]')].find(group => {
		const names = [...group.querySelectorAll('button')].map(b => b.textContent.trim().toLowerCase())
		return want.every(w => names.includes(w))
	}) || null
}

// The row that holds every toggle of the filter panel (the first ancestor with more than one selector)
function findTogglesRow() {
	let el = findToggle('Site', 'Market')
	while (el && el.querySelectorAll('[role="group"]').length < 2) el = el.parentElement
	return el
}

// A filter field: its cell in the filters grid, found by its exact label
function findField(...texts) {
	const want = texts.map(t => t.toLowerCase())
	const label = [...document.querySelectorAll('label')].find(l => want.includes(l.textContent.trim().toLowerCase()))
	return label?.closest('.MuiGrid-item') || label?.closest('.MuiFormControl-root') || null
}

// The CSS grid that lays out all the filter fields
function findFieldsGrid() {
	let el = findField('Providers')
	while (el && getComputedStyle(el).display !== 'grid') el = el.parentElement
	return el
}

// The results table (real or demo): the one whose header has the "Branch / Providers" corner
function findPricingTable() {
	const table = [...document.querySelectorAll('table')].find(t => t.querySelector('th')?.textContent.includes('Branch / Providers'))
	return table || null
}

// One box over several elements, for a step that talks about a column or a group of cells
function unionTarget(els) {
	const list = els.filter(Boolean)
	if (!list.length) return null
	return {
		scrollIntoView: opts => list[Math.floor(list.length / 2)].scrollIntoView(opts),
		getBoundingClientRect: () => {
			const rects = list.map(e => e.getBoundingClientRect())
			const left = Math.min(...rects.map(r => r.left)), top = Math.min(...rects.map(r => r.top))
			const right = Math.max(...rects.map(r => r.right)), bottom = Math.max(...rects.map(r => r.bottom))
			return { left, top, right, bottom, width: right - left, height: bottom - top }
		},
	}
}

// Body rows of the table: a grey branch row (the name and one empty cell) followed by its provider rows
function bodyRows() {
	return [...(findPricingTable()?.querySelectorAll('tbody tr') || [])]
}
const isBranchRow = row => row.cells.length <= 2 && !row.cells[1]?.textContent.trim()

// Left column of the first branch: the corner header, the branch row and its providers
function findOfficeColumn() {
	const table = findPricingTable()
	const rows = bodyRows()
	const first = rows.findIndex(isBranchRow)
	if (!table || first < 0) return null
	const group = [rows[first]]
	for (let i = first + 1; i < rows.length && !isBranchRow(rows[i]); i++) group.push(rows[i])
	return unionTarget([table.querySelector('th'), ...group.map(r => r.cells[0])])
}

// The price boxes of the first provider row under the first pickup date (all its booking weeks)
function findFirstDateCells() {
	const table = findPricingTable()
	const row = bodyRows().find(r => !isBranchRow(r))
	const dateHeader = table?.querySelectorAll('thead tr:first-child th')[1]
	if (!row || !dateHeader) return null
	const h = dateHeader.getBoundingClientRect()
	const cells = [...row.cells].slice(1).filter(c => {
		const r = c.getBoundingClientRect()
		const center = r.left + r.width / 2
		return center > h.left && center < h.right
	})
	return unionTarget(cells)
}

// The engine measures the target 250 ms after a smooth scroll starts, too early for a long scroll (filters at
// the top, table at the bottom): every target here jumps into view at once, so the spotlight lands on it.
function instant(el) {
	return el && {
		scrollIntoView: () => el.scrollIntoView({ block: 'center' }),
		getBoundingClientRect: () => el.getBoundingClientRect(),
	}
}

const ES = navigator.language?.startsWith('es')

const STEPS = [
	{
		type: 'intro',
		icon: 'table-cells-column-lock',
		title: ES ? 'Bienvenido al Entorno de Precios' : 'Welcome to Pricing Environment',
		description: ES
			? 'El Entorno de Precios ofrece una vista multidimensional de los precios de alquiler de coches por oficinas, proveedores, fechas de recogida y semanas de reserva. Compara precios de un vistazo y sigue la evolución a lo largo del tiempo con indicadores codificados por color.'
			: 'The Pricing Environment provides a multi-dimensional view of car rental prices across offices, providers, pickup dates, and booking weeks. Compare prices at a glance and track evolution over time with color-coded indicators.',
	},
	{
		type: 'field',
		findTarget: () => findTogglesRow(),
		icon: 'toggle-on',
		title: ES ? 'Selectores de los Filtros' : 'Filter Toggles',
		description: ES
			? 'Sobre los campos, cada selector elige cómo filtrar: precio total del alquiler o por día, oficinas sueltas o un grupo de oficinas, sitio o mercado, y sitios sueltos o un grupo de sitios.'
			: 'Above the fields, each toggle picks how you filter: total rental price or per day, single offices or an office group, site or market, and single sites or a site group.',
		example: ES
			? '«Total price» / «Daily» · «Offices» / «Office group» · «Site» / «Market» · «Sites» / «Site group»'
			: '"Total price" / "Daily" · "Offices" / "Office group" · "Site" / "Market" · "Sites" / "Site group"',
	},
	{
		type: 'field',
		findTarget: () => findToggle('Site', 'Market'),
		icon: 'toggle-on',
		title: ES ? 'Selector Sitio / Mercado' : 'Site / Market Toggle',
		description: ES
			? 'Alterna entre filtrar por sitios web de alquiler específicos (Sitio) o por región de mercado (Mercado). Esto cambia las opciones disponibles en el desplegable de sitio/mercado de abajo.'
			: 'Switch between filtering by specific rental websites (Site) or by market region (Market). This changes the available options in the site/market dropdown below.',
		example: ES
			? 'Modo Sitio: kayak.com, rentalcars.com | Modo Mercado: ES, UK, FR'
			: 'Site mode: kayak.com, rentalcars.com | Market mode: ES, UK, FR',
	},
	{
		type: 'field',
		findTarget: () => findField('Booking Weeks'),
		icon: 'calendar-week',
		title: ES ? 'Semanas de Reserva' : 'Booking Weeks',
		description: ES
			? 'Selecciona las semanas (por su número) en las que se recogieron los precios. Cada semana de reserva se convierte en una subcolumna bajo cada fecha de recogida, para ver cómo cambió el precio de una misma fecha de una semana a la siguiente.'
			: 'Select the weeks (by number) in which prices were collected. Each booking week becomes a sub-column under each pickup date, so you can see how the price for the same date changed from one week to the next.',
		example: ES
			? 'Semanas 40, 41, 42 → el precio de cada fecha tal como se vio en cada una de esas semanas'
			: 'Weeks 40, 41, 42 → the price of each date as seen in each of those weeks',
	},
	{
		type: 'field',
		findTarget: () => findField('Rental dates'),
		icon: 'calendar-days',
		title: ES ? 'Fechas de Recogida' : 'Pickup Dates',
		description: ES
			? 'Selecciona varias fechas de recogida para comparar. Cada fecha se convierte en un encabezado de columna principal en la tabla de resultados. Puedes añadir varias fechas para ver los precios en diferentes periodos de viaje.'
			: 'Select multiple pickup dates to compare. Each date becomes a main column header in the results table. You can add several dates to see pricing across different travel periods.',
	},
	{
		type: 'field',
		findTarget: () => findFieldsGrid(),
		icon: 'filter',
		title: ES ? 'Filtros de Búsqueda' : 'Search Filters',
		description: ES
			? 'Configura tu consulta de precios: selecciona oficinas, sitios/mercados, niveles, proveedores, categorías, duración del alquiler (LOR), código ACRISS, transmisión y moneda. Todos los campos acotan los resultados para mostrar exactamente lo que necesitas.'
			: 'Configure your pricing query: select offices, sites/markets, tiers, providers, categories, LOR, ACRISS code, transmission, and currency. All fields narrow down the results to show exactly what you need.',
		example: ES
			? 'Oficinas + Proveedores + Categorías + LOR + Moneda → «Search»'
			: 'Offices + Providers + Categories + LOR + Currency → "Search"',
	},
	{
		type: 'field',
		findTarget: () => findPricingTable()?.closest('.MuiCard-root') || null,
		icon: 'table-cells',
		title: ES ? 'Tabla de Precios' : 'Pricing Table',
		description: ES
			? 'La tabla principal de resultados. Cada oficina es una fila gris con sus proveedores debajo, en la columna fija de la izquierda. Cada columna de fecha se divide en subcolumnas por semana de reserva. Las celdas muestran precios con bordes codificados por color e indicadores de evolución.'
			: 'The main results table. Each office is a grey row with its providers under it, in the sticky left column. Each date column is split into booking week sub-columns. Cells show prices with color-coded borders and evolution indicators.',
		example: ES
			? 'Borde rojo = Más barato | Borde azul = Más caro | Gris = Neutro'
			: 'Red border = Cheapest | Blue border = Most Expensive | Grey = Neutral',
	},
	{
		type: 'field',
		findTarget: () => findToggle('Min. Price', 'Avg. Price', 'Max. Price'),
		skipIf: () => !findToggle('Min. Price', 'Avg. Price', 'Max. Price'),
		icon: 'sliders',
		title: ES ? 'Selector de Agregación de Precios' : 'Price Aggregation Toggle',
		description: ES
			? 'Alterna entre precio mínimo, medio y máximo. La tabla recalcula todas las celdas para mostrar la agregación seleccionada. Útil para comparar escenarios de precios en el mejor y peor caso.'
			: 'Switch between minimum, average, and maximum prices. The table recalculates all cells to show the selected aggregation. Useful for comparing best-case vs worst-case pricing scenarios.',
		example: '"Min. Price" | "Avg. Price" | "Max. Price"',
	},
	{
		type: 'field',
		findTarget: () => findOfficeColumn(),
		skipIf: () => !findOfficeColumn(),
		icon: 'building',
		title: ES ? 'Columna de Oficina y Proveedores' : 'Office & Providers Column',
		description: ES
			? 'Bajo la esquina «Branch / Providers», cada oficina abre una fila gris con sus proveedores listados debajo. Esta columna permanece fija mientras te desplazas horizontalmente por las columnas de fechas, manteniendo el contexto visible en todo momento.'
			: 'Under the "Branch / Providers" corner, each office opens a grey row with its providers listed below. This column stays fixed while you scroll horizontally through the date columns, keeping context visible at all times.',
	},
	{
		type: 'field',
		findTarget: () => findFirstDateCells(),
		skipIf: () => !findFirstDateCells(),
		icon: 'fire-flame-curved',
		title: ES ? 'Celdas de Precio y Evolución' : 'Price Cells & Evolution',
		description: ES
			? 'Cada celda muestra un precio con un borde de color: rojo para el más barato, azul para el más caro y gris para neutro. Debajo del precio, flechas indican la evolución respecto a la semana de reserva anterior — flecha verde arriba significa que el precio subió, flecha roja abajo que bajó, junto con el porcentaje de cambio.'
			: 'Each cell shows a price with a colored border: red for cheapest, blue for most expensive, and grey for neutral. Below the price, arrows indicate evolution compared to the previous booking week — a green up arrow means the price increased, a red down arrow that it decreased, with the percentage change.',
		example: ES
			? '42,30 € ↑ 5,2% (subió) | 38,10 € ↓ 3,1% (bajó)'
			: '€42.30 ↑ 5.2% (increased) | €38.10 ↓ 3.1% (decreased)',
	},
	{
		type: 'outro',
		icon: 'star',
		title: ES ? '¡Todo Listo!' : "You're All Set!",
		description: ES
			? 'Selecciona tus oficinas, proveedores, fechas y semanas de reserva, y pulsa «Search». Usa el selector Mín./Media/Máx. para cambiar de perspectiva. Desplázate horizontalmente para comparar fechas mientras la columna de oficinas permanece fija a la izquierda.'
			: 'Select your offices, providers, dates, and booking weeks, then hit "Search". Use the Min/Avg/Max toggle to switch perspectives. Scroll horizontally to compare across dates while the office column stays pinned on the left.',
	},
]

export const TOUR_STEPS = STEPS.map(step => step.findTarget ? { ...step, findTarget: () => instant(step.findTarget()) } : step)
